import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { prescriptionAPI, doctorAPI } from '../../services/api';
import { tokenManager } from '../../services/tokenManager';

const NewPrescription = () => {
  const navigate = useNavigate();
  
  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loadingPatients, setLoadingPatients] = useState(true);
  
  const [formData, setFormData] = useState({
    session: '',
    patient: '',
    medicationName: '',
    dosage: '',
    frequency: '',
    durationDays: '',
    specialInstructions: '',
    prescriptionFileUrl: '',
    eScriptId: '',
    issuedAt: new Date().toISOString(),
    status: 'active',
    route: '',
    startDate: new Date().toISOString().split('T')[0],
    refillsAllowed: 0
  });

  // Get current doctor ID
  const currentUser = tokenManager.getUser();
  const doctorId = currentUser?._id || currentUser?.id;

  // Add scrollbar CSS only once when component mounts
  // useEffect(() => {
  
  //   const existingStyle = document.getElementById('custom-scrollbar-style');
  //   if (!existingStyle) {
  //     const style = document.createElement('style');
  //     style.id = 'custom-scrollbar-style';
  //     style.innerHTML = `
  //       .custom-scrollbar {
  //         scrollbar-width: thin; /* Firefox */
  //         scrollbar-color: transparent transparent; /* Firefox */
  //       }

  //       .custom-scrollbar::-webkit-scrollbar {
  //         width: 8px; /* Width of the scrollbar */
  //       }

  //       .custom-scrollbar::-webkit-scrollbar-track {
  //         background: transparent; /* Track background */
  //       }

  //       .custom-scrollbar::-webkit-scrollbar-thumb {
  //         background: rgba(156, 163, 175, 0.3); /* Blur effect with low opacity */
  //         border-radius: 4px;
  //         transition: all 0.3s ease;
  //       }

  //       .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  //         background: rgba(156, 163, 175, 0.8); /* More visible on hover */
  //         backdrop-filter: blur(4px);
  //       }

  //       .custom-scrollbar::-webkit-scrollbar-thumb:active {
  //         background: rgba(156, 163, 175, 1); /* Fully visible when dragging */
  //       }

  //       /* For Firefox */
  //       .custom-scrollbar {
  //         scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
  //       }

  //       .custom-scrollbar:hover {
  //         scrollbar-color: rgba(156, 163, 175, 0.8) transparent;
  //       }
  //     `;
  //     document.head.appendChild(style);
  //   }

  //   // Cleanup function to remove the style when component unmounts
  //   return () => {
  //     const styleToRemove = document.getElementById('custom-scrollbar-style');
  //     if (styleToRemove) {
  //       styleToRemove.remove();
  //     }
  //   };
  // }, []); 

  useEffect(() => {
    if (doctorId) {
      fetchPatients();
    }
  }, [doctorId]);

  const fetchPatients = async () => {
    try {
      setLoadingPatients(true);
      setError(null);

      console.log('Fetching patients for doctor:', doctorId);
      const response = await doctorAPI.getPatientsForDoctor(doctorId);
      console.log('Patients response:', response);

      if (response.success) {
        setPatients(response.data || []);
      } else {
        setError(response.message || 'Failed to fetch patients');
        setPatients([]);
      }
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load patients');
      setPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  const handlePatientChange = (e) => {
    const patientId = e.target.value;
    setSelectedPatientId(patientId);
    
    if (patientId) {
      const patient = patients.find(p => p._id === patientId);
      setSelectedPatient(patient);
      setFormData(prev => ({
        ...prev,
        patient: patientId
      }));
    } else {
      setSelectedPatient(null);
      setFormData(prev => ({
        ...prev,
        patient: ''
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const requiredFields = [
      'patient', 'medicationName', 'dosage', 'frequency', 'durationDays', 
      'route', 'startDate', 'refillsAllowed'
    ];
    
    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(`Please fill in the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field`);
        return false;
      }
    }
    
    if (formData.durationDays < 1) {
      setError('Duration must be at least 1 day');
      return false;
    }
    
    if (formData.refillsAllowed < 0) {
      setError('Refills allowed cannot be negative');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare the data for API
      const prescriptionData = {
        ...formData,
        durationDays: parseInt(formData.durationDays),
        refillsAllowed: parseInt(formData.refillsAllowed),
        issuedAt: new Date().toISOString(),
        startDate: new Date(formData.startDate).toISOString()
      };

      console.log('Submitting prescription data:', prescriptionData);
      
      const response = await prescriptionAPI.createPrescription(prescriptionData);
      console.log('Prescription creation response:', response);

      if (response.success) {
        alert('Prescription created successfully!');
        navigate('/psy/prescriptions'); // Navigate back to prescriptions list
      } else {
        setError(response.message || 'Failed to create prescription');
      }
    } catch (err) {
      console.error('Error creating prescription:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create prescription');
    } finally {
      setLoading(false);
    }
  };

  // Skeleton loader for form submission
  const SubmitButtonSkeleton = () => (
    <div className="flex gap-4 justify-end">
      <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
      <div className="h-8 bg-gray-200 rounded w-32 animate-pulse"></div>
    </div>
  );

  // Loading state for initial page load
  if (loadingPatients) {
    return (
      <div className="min-h-screen py-10 px-4 sm:px-6 lg:px-8 custom-scrollbar">
        <div className="max-w-5xl mx-auto bg-white p-8 rounded-xl shadow-md border border-gray-200">
          {/* Header Skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
          </div>

          {/* Patient Selection Skeleton */}
          <div className="mb-6">
            <div className="h-4 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
          </div>

          {/* Patient Info Card Skeleton */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(3)].map((_, index) => (
                <div key={index}>
                  <div className="h-3 bg-gray-200 rounded w-20 mb-1 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-24 mb-1 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Form Fields Skeleton */}
          <div className="space-y-6">
            {/* Medication Name */}
            <div>
              <div className="h-4 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
            </div>

            {/* Form Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-md border border-gray-200">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                  <div className="h-10 bg-gray-200 rounded w-full mb-4 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2 animate-pulse"></div>
                  <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
                </div>
              ))}
            </div>

            {/* Additional Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(2)].map((_, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-md border border-gray-200">
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
                  <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
                </div>
              ))}
            </div>

            {/* Notes Section */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="h-4 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
              <div className="h-24 bg-gray-200 rounded w-full mb-6 animate-pulse"></div>
              
              {/* Footer Actions */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center">
                  <div className="h-4 w-4 bg-gray-200 rounded mr-2 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-48 animate-pulse"></div>
                </div>
                <div className="flex gap-4">
                  <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded w-32 animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && patients.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/psy/prescriptions')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="min-h-screen py-10 px-4 sm:px-6 lg:px-8 custom-scrollbar">
        <div className="max-w-5xl mx-auto bg-white p-8 rounded-xl shadow-md border border-gray-200">
          <h1 className="text-2xl font-semibold text-gray-800 mb-8">New Prescription</h1>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Patient Selection */}
            <div className="mb-6 text-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Patient *</label>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                value={selectedPatientId}
                onChange={handlePatientChange}
                required
              >
                <option value="">-- Choose a patient --</option>
                {patients.length === 0 ? (
                  <option value="" disabled>No patients assigned to you</option>
                ) : (
                  patients.map(patient => (
                    <option key={patient._id} value={patient._id}>
                      {patient.name} ({patient.age || 'N/A'} · {patient.gender || 'N/A'})
                    </option>
                  ))
                )}
              </select>
              {patients.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  You don't have any patients assigned. Please contact the administrator.
                </p>
              )}
            </div>

            {/* Patient Info Card */}
            {selectedPatient && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Patient Name</p>
                    <p className="font-medium text-gray-800">{selectedPatient.name}</p>
                    <p className="text-xs text-gray-500">#{selectedPatient._id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Age / Gender</p>
                    <p className="font-medium text-gray-800">
                      {selectedPatient.age || 'N/A'} · {selectedPatient.gender || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Assessment Status</p>
                    <p className="flex items-center gap-2 text-green-600 font-medium">
                      <span className="w-2 h-2 bg-green-500 rounded-full inline-block" />
                      {selectedPatient.assessmentStatus || 'Completed'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Search Medication */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Medication Name *
              </label>
              <input
                type="text"
                name="medicationName"
                value={formData.medicationName}
                onChange={handleInputChange}
                placeholder="Search medication..."
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Dosage & Route */}
              <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Dosage *</label>
                <input
                  type="text"
                  name="dosage"
                  value={formData.dosage}
                  onChange={handleInputChange}
                  placeholder="Enter dosage (e.g., 10mg)"
                  className="w-full mb-4 px-4 py-2 border border-gray-300 rounded-md text-sm"
                  required
                />

                <label className="block text-sm font-medium text-gray-700 mb-2">Route *</label>
                <div className="relative w-full mb-4">
                  <select 
                    name="route"
                    value={formData.route}
                    onChange={handleInputChange}
                    className="w-full appearance-none px-4 py-2 border border-gray-300 rounded-md text-sm bg-white"
                    required
                  >
                    <option value="">Select route</option>
                    <option value="Oral">Oral</option>
                    <option value="Intramuscular">Intramuscular</option>
                    <option value="Subcutaneous">Subcutaneous</option>
                    <option value="Buccal">Buccal</option>
                    <option value="Topical">Topical</option>
                    <option value="Inhalation">Inhalation</option>
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <svg
                      className="w-4 h-4 text-black"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Frequency & Start Date */}
              <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Frequency *</label>
                <div className="relative w-full mb-4">
                  <select 
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleInputChange}
                    className="w-full appearance-none px-4 py-2 border border-gray-300 rounded-md text-sm bg-white"
                    required
                  >
                    <option value="">Select frequency</option>
                    <option value="Once daily">Once daily</option>
                    <option value="Twice daily">Twice daily</option>
                    <option value="Three times daily">Three times daily</option>
                    <option value="As needed">As needed</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                  </select>

                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <svg
                      className="w-4 h-4 text-black"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
                  required
                />
              </div>

              {/* Duration */}
              <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Duration (days) *</label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    name="durationDays"
                    value={formData.durationDays}
                    onChange={handleInputChange}
                    placeholder="Enter duration"
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
                    required
                  />
                  <span className="px-4 py-2 text-sm text-gray-600 flex items-center">days</span>
                </div>
              </div>

              {/* Refills */}
              <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Refills Allowed *</label>
                <input
                  type="number"
                  name="refillsAllowed"
                  value={formData.refillsAllowed}
                  onChange={handleInputChange}
                  placeholder="0–5"
                  min="0"
                  max="10"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
                  required
                />
              </div>
            </div>

            {/* Additional Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* eScript ID */}
              {/* <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">eScript ID (Optional)</label>
                <input
                  type="text"
                  name="eScriptId"
                  value={formData.eScriptId}
                  onChange={handleInputChange}
                  placeholder="Enter eScript ID"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div> */}

              {/* Prescription File URL */}
              {/* <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Prescription File URL (Optional)</label>
                <input
                  type="url"
                  name="prescriptionFileUrl"
                  value={formData.prescriptionFileUrl}
                  onChange={handleInputChange}
                  placeholder="https://example.com/prescription.pdf"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div> */}
            </div>

            {/* Notes & Actions */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mt-6">
              {/* Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes & Instructions
                </label>
                <textarea
                  name="specialInstructions"
                  value={formData.specialInstructions}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                  placeholder="Enter any special instructions or notes..."
                ></textarea>
              </div>

              {/* Footer Actions */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center text-sm text-gray-700">
                  <input type="checkbox" id="pdf" className="mr-2" />
                  <label htmlFor="pdf">Generate printable PDF after submit</label>
                </div>

                {loading ? (
                  <SubmitButtonSkeleton />
                ) : (
<div className="flex gap-4 justify-end">
  <button 
    type="button"
    onClick={() => navigate('/psy/prescriptions')}
    className="text-gray-600 hover:text-gray-800 text-sm"
  >
    Cancel
  </button>
  <button
    type="submit"
    disabled={loading || patients.length === 0}
    className={`px-4 py-2 text-sm rounded text-white ${
      loading || patients.length === 0
        ? 'bg-gray-400 cursor-not-allowed'
        : 'bg-blue-500 hover:bg-blue-600'
    }`}
  >
    {loading ? 'Creating...' : 'Create Prescription'}
  </button>
</div>


                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewPrescription;
